import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { FracturedHalo } from './FracturedHalo';
import { TetrisBackground } from './TetrisBackground';
export const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.8]);
  // Enhanced sunrise glow that persists and blends smoothly
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0.8, 0.6, 0.4, 0.3]);
  // Parallax layers for depth
  const layer1Y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const layer2Y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const layer3Y = useTransform(scrollYProgress, [0, 1], ['0%', '70%']);
  return <motion.section ref={sectionRef} className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 1
  }} style={{
    opacity
  }} data-id="element-323">
      {/* Enhanced Particle Background - Behind everything */}
      <div className="absolute inset-0 z-0" data-id="element-324">
        <TetrisBackground data-id="element-325" />
      </div>

      {/* Data Stream Lines - Flowing Energy */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-[2]" style={{
      opacity: 0.4
    }} data-id="element-326">
        <defs data-id="element-327">
          <linearGradient id="streamGradient1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-328">
            <stop offset="0%" stopColor="#faff31" stopOpacity="0" data-id="element-329" />
            <stop offset="50%" stopColor="#faff31" stopOpacity="0.6" data-id="element-330" />
            <stop offset="100%" stopColor="#faff31" stopOpacity="0" data-id="element-331" />
          </linearGradient>
          <linearGradient id="streamGradient2" x1="100%" y1="0%" x2="0%" y2="100%" data-id="element-332">
            <stop offset="0%" stopColor="#15c8ff" stopOpacity="0" data-id="element-333" />
            <stop offset="50%" stopColor="#15c8ff" stopOpacity="0.6" data-id="element-334" />
            <stop offset="100%" stopColor="#15c8ff" stopOpacity="0" data-id="element-335" />
          </linearGradient>
          <linearGradient id="streamGradient3" x1="0%" y1="100%" x2="100%" y2="0%" data-id="element-336">
            <stop offset="0%" stopColor="#4f0c9a" stopOpacity="0" data-id="element-337" />
            <stop offset="50%" stopColor="#4f0c9a" stopOpacity="0.6" data-id="element-338" />
            <stop offset="100%" stopColor="#4f0c9a" stopOpacity="0" data-id="element-339" />
          </linearGradient>
        </defs>

        {/* Flowing data streams */}
        <motion.path d="M0,200 Q400,150 800,200 T1600,200" stroke="url(#streamGradient1)" strokeWidth="2" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: [0, 1, 0],
        opacity: [0, 0.8, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-340" />
        <motion.path d="M1600,400 Q1200,350 800,400 T0,400" stroke="url(#streamGradient2)" strokeWidth="2" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: [0, 1, 0],
        opacity: [0, 0.8, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-341" />
        <motion.path d="M0,600 Q400,550 800,600 T1600,600" stroke="url(#streamGradient3)" strokeWidth="2" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: [0, 1, 0],
        opacity: [0, 0.8, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-342" />
      </svg>

      {/* Enhanced Sunrise Glow Effect - Multi-layered for smooth blending */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[70vh] pointer-events-none z-[3]" style={{
      background: 'radial-gradient(ellipse 90% 60% at 50% 100%, rgba(250, 255, 49, 0.2) 0%, rgba(250, 255, 49, 0.1) 40%, transparent 70%)',
      opacity: sunriseOpacity
    }} data-id="element-343" />

      {/* Additional layered glows for depth and smooth blending */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none z-[3]" style={{
      background: 'radial-gradient(ellipse 100% 50% at 50% 100%, rgba(250, 255, 49, 0.15) 0%, transparent 60%)',
      opacity: sunriseOpacity,
      y: layer1Y
    }} data-id="element-344" />

      <motion.div className="absolute bottom-0 left-0 right-0 h-[40vh] pointer-events-none z-[3]" style={{
      background: 'radial-gradient(ellipse 80% 40% at 50% 100%, rgba(21, 200, 255, 0.1) 0%, transparent 60%)',
      opacity: sunriseOpacity,
      y: layer2Y
    }} data-id="element-345" />

      {/* Side glows with parallax */}
      <motion.div className="absolute left-0 top-1/2 -translate-y-1/2 w-[40vw] h-[60vh] pointer-events-none z-[3]" style={{
      background: 'radial-gradient(ellipse 60% 80% at 0% 50%, rgba(250, 255, 49, 0.15) 0%, transparent 60%)',
      opacity: sunriseOpacity,
      x: layer1Y
    }} data-id="element-346" />
      <motion.div className="absolute right-0 top-1/2 -translate-y-1/2 w-[40vw] h-[60vh] pointer-events-none z-[3]" style={{
      background: 'radial-gradient(ellipse 60% 80% at 100% 50%, rgba(250, 255, 49, 0.15) 0%, transparent 60%)',
      opacity: sunriseOpacity,
      x: layer3Y
    }} data-id="element-347" />

      {/* Enhanced Floating Energy Orbs - More yellow and blue dots */}
      {[...Array(12)].map((_, i) => <motion.div key={`orb-${i}`} className="absolute w-2 h-2 pointer-events-none z-[2]" style={{
      left: `${10 + i * 7 % 80}%`,
      top: `${20 + i * 11 % 60}%`,
      background: i % 2 === 0 ? 'radial-gradient(circle, rgba(250, 255, 49, 0.9) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(21, 200, 255, 0.9) 0%, transparent 70%)',
      boxShadow: i % 2 === 0 ? '0 0 20px rgba(250, 255, 49, 0.8)' : '0 0 20px rgba(21, 200, 255, 0.8)'
    }} animate={{
      y: [0, -25 - i % 3 * 10, 0],
      x: [0, Math.sin(i) * 15, 0],
      scale: [1, 1.3, 1],
      opacity: [0.5, 0.9, 0.5]
    }} transition={{
      duration: 3 + i * 0.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.2
    }} data-id="element-348" />)}

      <motion.div className="container mx-auto px-4 z-10 text-center pointer-events-none" style={{
      y,
      scale
    }} data-id="element-349">
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-350">
          <div className="relative inline-block" data-id="element-351">
            {/* Enhanced Halo System with more layers */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-352">
              {/* Outer breathing ring */}
              <motion.div className="absolute" animate={{
              scale: [1, 1.08, 1],
              opacity: [0.2, 0.4, 0.2],
              rotate: [0, 180, 360]
            }} transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-353">
                <FracturedHalo size={800} segments={20} opacity={0.3} animate={true} vortexAnimation={false} color="#4f0c9a" data-id="element-354" />
              </motion.div>

              {/* Middle pulsing ring */}
              <motion.div className="absolute" animate={{
              scale: [1, 1.05, 1],
              opacity: [0.3, 0.5, 0.3],
              rotate: [0, -90, -180]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-355">
                <FracturedHalo size={700} segments={16} opacity={0.4} animate={true} vortexAnimation={false} color="#4f0c9a" data-id="element-356" />
              </motion.div>

              {/* Vortex ring */}
              <motion.div className="absolute" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 1.5,
              delay: 0.5
            }} data-id="element-357">
                <FracturedHalo size={600} segments={12} opacity={0.8} animate={true} vortexAnimation={true} color="#faff31" data-id="element-358" />
              </motion.div>

              {/* Inner rotating ring */}
              <motion.div className="absolute" animate={{
              rotate: 360,
              scale: [1, 1.03, 1]
            }} transition={{
              rotate: {
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              },
              scale: {
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} data-id="element-359">
                <FracturedHalo size={500} segments={8} opacity={0.6} animate={true} vortexAnimation={false} color="#1f1577" data-id="element-360" />
              </motion.div>
            </div>

            <div className="relative flex flex-col items-center gap-6" data-id="element-361">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 0.5
            }} data-id="element-362">
                <motion.img src="https://cdn.magicpatterns.com/uploads/qP1KSUaSZPjqPLU8SY6NB5/LOGO-A1.png" alt="Deploid" className="w-full max-w-2xl mx-auto" style={{
                filter: 'drop-shadow(0 0 40px rgba(250, 255, 49, 0.4))'
              }} animate={{
                filter: ['drop-shadow(0 0 40px rgba(250, 255, 49, 0.4))', 'drop-shadow(0 0 60px rgba(250, 255, 49, 0.6))', 'drop-shadow(0 0 40px rgba(250, 255, 49, 0.4))']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-363" />
              </motion.div>

              {/* Headline */}
              <motion.h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight max-w-4xl px-4" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 1.0
            }} data-id="element-364">
                Where <span className="text-electric-yellow" data-id="element-365">strategy</span>,{' '}
                <span className="text-electric-yellow" data-id="element-366">systems</span>, and{' '}
                <span className="text-electric-yellow" data-id="element-367">execution</span> come
                together.
              </motion.h1>

              {/* Subtext */}
              <motion.p className="text-base md:text-lg lg:text-xl text-white/90 max-w-3xl leading-relaxed px-4" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 1.2
            }} data-id="element-368">
                We design, build, and evolve intelligent business systems across
                CRM, ERP, AI, and bespoke solutions — starting with the outcome,
                not the tool.
              </motion.p>

              {/* Badges */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 1.4
            }} className="flex flex-wrap items-center justify-center gap-3 mt-2" data-id="element-369">
                <div className="relative" data-id="element-370">
                  <div className="relative px-4 py-1.5 rounded-full border backdrop-blur-sm" style={{
                  borderColor: 'rgba(100, 120, 140, 0.3)',
                  backgroundColor: 'rgba(100, 120, 140, 0.08)'
                }} data-id="element-371">
                    <span className="text-xs md:text-sm font-medium tracking-wide whitespace-nowrap" style={{
                    color: 'rgba(150, 170, 190, 0.7)'
                  }} data-id="element-372">
                      System Design & Delivery
                    </span>
                  </div>
                </div>

                <div className="relative" data-id="element-373">
                  <div className="relative px-4 py-1.5 rounded-full border backdrop-blur-sm" style={{
                  borderColor: 'rgba(140, 140, 120, 0.3)',
                  backgroundColor: 'rgba(140, 140, 120, 0.08)'
                }} data-id="element-374">
                    <span className="text-xs md:text-sm font-medium tracking-wide whitespace-nowrap" style={{
                    color: 'rgba(180, 180, 160, 0.7)'
                  }} data-id="element-375">
                      CRM · ERP · AI · Bespoke
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator with enhanced animation */}
      <motion.div className="absolute bottom-8 left-0 right-0 w-full flex justify-center items-center z-10 pointer-events-none text-center" initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 1.5
    }} style={{
      opacity: useTransform(scrollYProgress, [0, 0.2], [1, 0])
    }} data-id="element-376">
        <motion.div animate={{
        y: [0, 12, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="flex flex-col items-center justify-center gap-2 mx-auto" data-id="element-377">
          <span className="text-white/60 text-sm tracking-wider text-center" data-id="element-378">
            SCROLL
          </span>
          <motion.svg className="w-6 h-6 text-electric-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-379">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-380" />
          </motion.svg>
        </motion.div>
      </motion.div>
    </motion.section>;
};